/**
 * Phase 7.OA-2 — Owner Console Dashboard v1.1.
 *
 * Adds, on top of the v1 cards:
 *   * tooltips on every KPI card explaining what it counts and why
 *   * a date-range selector (Today / 7D / 30D / 90D / YTD / All / Custom)
 *     that drives the metric cards + the four chart panels
 *   * four minimal recharts panels (user growth, analyses over time,
 *     plan distribution, top tickers) and an honest empty-state for
 *     the revenue-trend chart until CopeCart webhooks ship
 *   * compact-but-debuggable audit log row with old→new diff
 *
 * Pure frontend — does NOT touch the analyze pipeline, FMP, AI, cache,
 * pricing or paywall logic.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, BarChart3, Banknote, CalendarDays, CheckCircle2,
  ClipboardList, Database, Eye, Gauge, RefreshCw, ShieldCheck,
  Sparkles, TrendingUp, UserPlus, Users, Info, ChevronDown,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getTips } from "./tooltip_copy";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from "recharts";

// WS-8: admin credential is an HttpOnly ``admin_session`` cookie sent
// automatically via ``withCredentials: true``. WS-8 correction: attach
// the non-secret ``X-Admin-Request: 1`` header on every admin request
// so state-changing calls satisfy the interim CSRF barrier that WS-5
// will replace.
function adminHeaders() {
  return { "X-Admin-Request": "1" };
}
function fmtEur(n) {
  if (n === null || n === undefined) return "—";
  return `€${Number(n).toLocaleString("en-DE", { maximumFractionDigits: 0 })}`;
}
function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}
function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 30) return "just now";
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

/* =================== TOOLTIP COPY =================== */
// getTips() is imported from ./tooltip_copy to keep it trivially
// testable without a React/JSX toolchain.

/* =================== UI BUILDING BLOCKS =================== */
function InfoTip({ text }) {
  if (!text) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="What does this mean?"
            className="inline-flex items-center justify-center w-3.5 h-3.5 text-muted-foreground/70 hover:text-foreground transition-colors"
            data-testid="info-tip-trigger"
          >
            <Info className="w-3 h-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-[280px] text-[12px] leading-snug bg-popover text-popover-foreground border border-border"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MetricCard({ icon: Icon, label, value, sub, accent, testId, tip }) {
  const accentColor =
    accent === "green" ? "#3FAE89" :
    accent === "blue" ? "#3182CE" :
    accent === "amber" ? "#E0B341" :
    accent === "red" ? "#E5484D" :
    accent === "violet" ? "#9E7DD4" : "#7D8592";
  return (
    <div
      className="rounded-2xl border border-border/70 bg-card/60 p-4 hover:border-border transition-colors"
      data-testid={testId}
      data-tip={tip || ""}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg border"
          style={{ background: `${accentColor}1F`, borderColor: `${accentColor}59` }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: accentColor }} />
        </span>
        {tip ? <InfoTip text={tip} /> : null}
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {label}
      </div>
      <div className="mt-0.5 text-xl font-semibold text-foreground tabular-nums">{value}</div>
      {sub ? <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle, action, tip }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        <div className="flex items-center gap-1.5 text-foreground">
          {Icon ? <Icon className="w-4 h-4 text-muted-foreground" /> : null}
          <h3 className="text-sm font-semibold">{title}</h3>
          {tip ? <InfoTip text={tip} /> : null}
        </div>
        {subtitle ? <div className="text-[12px] text-muted-foreground mt-0.5">{subtitle}</div> : null}
      </div>
      {action}
    </div>
  );
}

function EmptyState({ text, testId }) {
  return (
    <div className="p-6 text-center text-sm text-muted-foreground" data-testid={testId}>
      {text}
    </div>
  );
}

/* =================== DATE RANGE SELECTOR =================== */
const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d",    label: "Last 7 days" },
  { value: "30d",   label: "Last 30 days" },
  { value: "90d",   label: "Last 90 days" },
  { value: "ytd",   label: "Year-to-date" },
  { value: "all",   label: "All time" },
];

function RangeSelector({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-8 w-[160px] text-xs bg-card/60 border-border/70"
        data-testid="owner-range-select"
      >
        <CalendarDays className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
        <SelectValue placeholder="Last 30 days" />
        <ChevronDown className="w-3 h-3 opacity-50 ml-auto" />
      </SelectTrigger>
      <SelectContent className="bg-popover text-popover-foreground border-border">
        {RANGE_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs" data-testid={`range-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* =================== REAL USERS ONLY TOGGLE =================== */
/**
 * Phase 7.OA-3.1 — "Customer metrics only" filter that excludes
 * owner/admin/test/seed accounts from the business KPIs and charts.
 * Default ON.
 *
 * IMPORTANT: This toggle affects ANALYTICS DISPLAY ONLY. It does NOT:
 *   • modify users          • delete users
 *   • change plans          • affect access rights
 *   • affect real billing   • affect subscription status
 *
 * Users tab is independent — admins can still see/filter every account
 * type there, including the ones excluded here.
 */
function RealUsersToggle({ value, onChange }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 pl-3 pr-2.5 py-1 h-8 cursor-default"
            data-testid="owner-real-users-toggle-wrap"
          >
            <Label
              htmlFor="owner-real-users-only"
              className="text-[11.5px] font-medium text-foreground/90 select-none cursor-pointer"
            >
              Customer metrics only
            </Label>
            <Switch
              id="owner-real-users-only"
              checked={!!value}
              onCheckedChange={(v) => onChange(!!v)}
              data-testid="owner-real-users-toggle"
              className="data-[state=checked]:bg-[#3FAE89] scale-90 -mr-1"
            />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="max-w-[260px] text-[11.5px] leading-snug bg-popover text-popover-foreground border border-border"
        >
          Excludes owner, admin, test and seed accounts from business KPIs.
          Display-only — never modifies users.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* =================== CHARTS =================== */
const CHART_AXIS_STYLE = { fontSize: 10, fill: "#7D8592" };
const CHART_GRID_STROKE = "rgba(125, 133, 146, 0.15)";

function chartPanel({ title, tip, height = 200, children, testId, hasData, emptyText }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card/40 p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-3">
        <h4 className="text-[12.5px] font-semibold text-foreground">{title}</h4>
        {tip ? <InfoTip text={tip} /> : null}
      </div>
      {hasData ? (
        <div style={{ width: "100%", height }}>{children}</div>
      ) : (
        <EmptyState text={emptyText || "Not enough data for this period yet."} testId={`${testId}-empty`} />
      )}
    </div>
  );
}

function UserGrowthChart({ data, height = 200, tip }) {
  const hasData = (data?.series || []).length > 0;
  return chartPanel({
    title: "User growth",
    tip,
    testId: "chart-user-growth",
    hasData,
    height,
    children: (
      <ResponsiveContainer>
        <LineChart data={data?.series || []} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_STROKE} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} width={28} />
          <RechartsTooltip
            contentStyle={{ background: "rgba(15, 19, 26, 0.96)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: "#7D8592" }}
          />
          <Line type="monotone" dataKey="new" stroke="#3FAE89" strokeWidth={2} dot={false} name="New users" />
          <Line type="monotone" dataKey="cumulative" stroke="#3182CE" strokeWidth={1.5} dot={false} strokeDasharray="4 3" name="Cumulative" />
        </LineChart>
      </ResponsiveContainer>
    ),
  });
}

function AnalysesChart({ data, height = 200, tip }) {
  const hasData = (data?.series || []).length > 0;
  return chartPanel({
    title: "Analyses over time",
    tip,
    testId: "chart-analyses",
    hasData,
    height,
    children: (
      <ResponsiveContainer>
        <BarChart data={data?.series || []} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_STROKE} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} width={28} />
          <RechartsTooltip
            contentStyle={{ background: "rgba(15, 19, 26, 0.96)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: "#7D8592" }}
          />
          <Bar dataKey="count" fill="#9E7DD4" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ),
  });
}

function PlanDistributionChart({ metrics, height = 200, tip }) {
  if (!metrics) return null;
  const data = [
    { name: "Free", value: metrics.free_users, fill: "#7D8592" },
    { name: "Pro", value: metrics.pro_users, fill: "#3182CE" },
    { name: "Premium", value: metrics.premium_users, fill: "#9E7DD4" },
  ];
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  return chartPanel({
    title: "Account plan distribution",
    tip,
    testId: "chart-plan-distribution",
    hasData: total > 0,
    height,
    children: (
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 8, top: 4, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_STROKE} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} />
          <YAxis dataKey="name" type="category" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} width={56} />
          <RechartsTooltip
            cursor={{ fill: "rgba(255,255,255,0.04)" }}
            contentStyle={{ background: "rgba(15, 19, 26, 0.96)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    ),
  });
}

function RevenueChart({ data, height = 200, tip }) {
  const hasData = (data?.series || []).length > 1; // single point is just current MRR
  return chartPanel({
    title: "Revenue trend",
    tip,
    testId: "chart-revenue",
    hasData,
    height,
    emptyText: data?.note || "No CopeCart revenue yet.",
    children: (
      <ResponsiveContainer>
        <LineChart data={data?.series || []} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_STROKE} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} />
          <YAxis tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} width={36} />
          <RechartsTooltip
            contentStyle={{ background: "rgba(15, 19, 26, 0.96)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
          />
          <Line type="monotone" dataKey="mrr_eur" stroke="#3FAE89" strokeWidth={2} dot={true} name="MRR (€)" />
        </LineChart>
      </ResponsiveContainer>
    ),
  });
}

function TopTickersChart({ data, height = 200, tip }) {
  const hasData = (data?.items || []).length > 0;
  return chartPanel({
    title: "Top analyzed tickers",
    tip,
    testId: "chart-top-tickers",
    hasData,
    height,
    children: (
      <ResponsiveContainer>
        <BarChart data={data?.items || []} layout="vertical" margin={{ left: 8, right: 8, top: 4, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_STROKE} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} />
          <YAxis dataKey="ticker" type="category" tick={CHART_AXIS_STYLE} tickLine={false} axisLine={false} width={56} />
          <RechartsTooltip
            cursor={{ fill: "rgba(255,255,255,0.04)" }}
            contentStyle={{ background: "rgba(15, 19, 26, 0.96)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }}
          />
          <Bar dataKey="count" fill="#3182CE" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ),
  });
}

/* =================== AUDIT ROW =================== */
function AuditDiff({ row }) {
  const o = row.old_value || {};
  const n = row.new_value || {};
  const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]));
  if (!keys.length) return null;
  return (
    <div className="mt-1 text-[11px] text-muted-foreground font-mono break-words leading-snug">
      {keys.slice(0, 4).map((k) => {
        const a = o[k]; const b = n[k];
        if (a === undefined && b === undefined) return null;
        if (JSON.stringify(a) === JSON.stringify(b)) return null;
        return (
          <div key={k}>
            <span className="text-foreground/70">{k}:</span>{" "}
            <span className="text-red-300/80">{String(a ?? "—")}</span>{" → "}
            <span className="text-green-300/80">{String(b ?? "—")}</span>
          </div>
        );
      })}
    </div>
  );
}

/* =================== MAIN =================== */
export default function OwnerDashboard() {
  const [range, setRange] = useState("30d");
  // Phase 7.OA-3 — "Real users only" toggle. Default ON so the Overview
  // tab shows real customer behavior straight away. The toggle ONLY
  // affects analytics display — backend never modifies users.
  const [realUsersOnly, setRealUsersOnly] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const tips = useMemo(() => getTips(realUsersOnly), [realUsersOnly]);
  const [highUsage, setHighUsage] = useState({ items: [] });
  const [audit, setAudit] = useState({ items: [] });
  const [growth, setGrowth] = useState({ series: [] });
  const [analyses, setAnalyses] = useState({ series: [] });
  const [tickers, setTickers] = useState({ items: [] });
  const [revenue, setRevenue] = useState({ series: [], note: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Phase 7.OA-3 — every analytics call carries the same
      // real_users_only flag so the entire Overview tab stays
      // internally consistent.
      const baseParams = { range, real_users_only: realUsersOnly };
      const cfg = { headers: adminHeaders(), params: baseParams };
      const [m, hu, a, g, an, tt, rev] = await Promise.all([
        api.get("/admin/metrics", cfg),
        api.get("/admin/usage/high", {
          headers: adminHeaders(),
          params: { limit: 8, days: rangeToDays(range), real_users_only: realUsersOnly },
        }),
        api.get("/admin/audit-log", { headers: adminHeaders(), params: { limit: 8 } }),
        api.get("/admin/analytics/user-growth", cfg),
        api.get("/admin/analytics/analyses-over-time", cfg),
        api.get("/admin/analytics/top-tickers", {
          ...cfg,
          params: { ...baseParams, limit: 8 },
        }),
        api.get("/admin/analytics/revenue-trend", cfg),
      ]);
      setMetrics(m.data || null);
      setHighUsage(hu.data || { items: [] });
      setAudit(a.data || { items: [] });
      setGrowth(g.data || { series: [] });
      setAnalyses(an.data || { series: [] });
      setTickers(tt.data || { items: [] });
      setRevenue(rev.data || { series: [], note: "" });
    } catch (err) {
      toast.error("Dashboard fetch failed", {
        description: err?.response?.data?.detail || err?.message || "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [range, realUsersOnly]);

  useEffect(() => { load(); }, [load]);

  const rangeLabel = useMemo(
    () => RANGE_OPTIONS.find((o) => o.value === range)?.label || "Last 30 days",
    [range],
  );

  if (!metrics) {
    return (
      <div className="rounded-2xl border border-border/70 bg-card/40 p-8 text-center text-sm text-muted-foreground" data-testid="owner-dashboard-loading">
        <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
        Loading Owner Console dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      {/* HEADER */}
      <section>
        <SectionTitle
          icon={Sparkles}
          title="Overview"
          subtitle={`Updated ${fmtRelative(metrics.generated_at)} · ${rangeLabel}${
            metrics?.filter?.real_users_only && metrics?.filter?.excluded_user_count
              ? ` · Excluding ${metrics.filter.excluded_user_count} internal account${metrics.filter.excluded_user_count === 1 ? "" : "s"}`
              : ""
          }`}
          action={
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <RealUsersToggle value={realUsersOnly} onChange={setRealUsersOnly} />
              <RangeSelector value={range} onChange={setRange} />
              <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="owner-dashboard-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          }
        />
        {/* Phase 7.OA-3.1 — neutral notice when toggle is ON and there are
            no real customers yet. Helps the operator interpret all-zero
            KPIs as "honest state" rather than "broken dashboard". */}
        {metrics?.filter?.real_users_only && metrics?.total_users === 0 ? (
          <div
            className="mb-3 rounded-xl border border-border/60 bg-secondary/20 px-3 py-2 text-[11.5px] text-muted-foreground inline-flex items-center gap-1.5"
            data-testid="owner-overview-no-customers-note"
          >
            <Info className="w-3 h-3" />
            No external customer data yet. Turn off &quot;Customer metrics only&quot; to see all accounts including owner/admin/test.
          </div>
        ) : null}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <MetricCard
            icon={Users}
            label="Verified external users"
            value={fmtNum(metrics.verified_external_users ?? 0)}
            sub="Positive external-verification signal required"
            accent="green"
            testId="metric-verified-external-users"
            tip="Genuine external customers with a positive verification signal. Confirmed test fixtures and unverified accounts are NOT included. Currently 0 until a verification signal is defined."
          />
          <MetricCard
            icon={ClipboardList}
            label="Accounts requiring review"
            value={fmtNum(metrics.accounts_requiring_review ?? metrics.unexcluded_accounts ?? metrics.total_users)}
            sub="Neither verified nor confirmed-test"
            accent="amber"
            testId="metric-accounts-requiring-review"
            tip="Unexcluded accounts that could not be positively verified as genuine external users. Confirmed QA test fixtures (payment verification = rejected_test) are automatically excluded and do not appear here."
          />
          <MetricCard icon={UserPlus} label="New in range" value={fmtNum(metrics.new_users_in_range)} sub={`${fmtNum(metrics.new_users_today)} today · ${fmtNum(metrics.new_users_7d)} in 7d`} accent="blue" testId="metric-new-users-today" tip={tips.new_users} />
          <MetricCard icon={Activity} label="Active 24h" value={fmtNum(metrics.active_users_24h)} sub={`${fmtNum(metrics.active_users_in_range)} in range · ${fmtNum(metrics.active_users_7d)} in 7d`} accent="amber" testId="metric-active-24h" tip={tips.active_24h} />
          <MetricCard icon={Eye} label="Analyses in range" value={fmtNum(metrics.analyses_in_range)} sub={`${fmtNum(metrics.analyses_today)} today · avg ${metrics.avg_analyses_per_user_in_range}/user`} accent="violet" testId="metric-analyses-today" tip={tips.analyses_range} />
        </div>
      </section>

      {/* CHARTS — moved up in v1.1.1: dashboard feels like a SaaS
          analytics view immediately after opening. */}
      <section>
        <SectionTitle icon={BarChart3} title="Trends" subtitle={`Bucketed ${growth?.granularity || "daily"} · ${rangeLabel}`} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <UserGrowthChart data={growth} height={260} tip={tips.charts_user_growth} />
          <AnalysesChart data={analyses} height={260} tip={tips.charts_analyses} />
          <PlanDistributionChart metrics={metrics} height={210} tip={tips.charts_plan_dist} />
          <RevenueChart data={revenue} height={210} tip={tips.charts_revenue} />
          <div className="lg:col-span-2">
            <TopTickersChart data={tickers} height={240} tip={tips.charts_top_tickers} />
          </div>
        </div>
      </section>

      {/* PLAN SPLIT */}
      <section>
        <SectionTitle
          icon={ShieldCheck}
          title="Account plan distribution"
          subtitle="Includes unverified and review accounts; not a customer or paid-plan split. Confirmed-test QA fixtures are excluded."
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard icon={Users} label="Free" value={fmtNum(metrics.free_users)} accent="default" testId="metric-free-users" tip={tips.free_users} />
          <MetricCard icon={Sparkles} label="Pro" value={fmtNum(metrics.pro_users)} accent="blue" testId="metric-pro-users" tip={tips.pro_users} />
          <MetricCard icon={Sparkles} label="Premium" value={fmtNum(metrics.premium_users)} accent="violet" testId="metric-premium-users" tip={tips.premium_users} />
          <MetricCard icon={Database} label="Watchlist items" value={fmtNum(metrics.watchlist_items_total)} accent="amber" testId="metric-watchlist-items" tip={tips.watchlist_items} />
        </div>
      </section>

      {/* REVENUE */}
      <section>
        <SectionTitle
          icon={Banknote}
          title="Revenue (CopeCart only)"
          subtitle="Manual access / promo / admin upgrades are excluded by design"
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard icon={TrendingUp} label="Verified paying customers" value={fmtNum(metrics.verified_paying_users ?? metrics.paying_users)} sub={metrics.payments_requiring_review > 0 ? `${fmtNum(metrics.payments_requiring_review)} require review · ${fmtNum(metrics.rejected_test_paid_users || 0)} rejected as test` : "no unverified claims"} accent="green" testId="metric-paying-users" tip={tips.paying_users} />
          <MetricCard icon={Banknote} label="Est. MRR (verified)" value={fmtEur(metrics.estimated_mrr_eur)} sub="EUR — verified CopeCart only" accent="green" testId="metric-mrr" tip={tips.mrr} />
          <MetricCard icon={CalendarDays} label="Est. ARR (verified)" value={fmtEur(metrics.estimated_arr_eur)} sub="MRR × 12 — verified only" accent="green" testId="metric-arr" tip={tips.arr} />
          <MetricCard icon={ClipboardList} label="Payments requiring review" value={fmtNum(metrics.payments_requiring_review || 0)} sub={`Manual ${metrics.manual_users} · Promo ${metrics.promo_users}`} accent="amber" testId="metric-manual-promo" tip={tips.manual_promo} />
        </div>
        <div className="mt-3 rounded-xl border border-border/50 bg-secondary/20 p-3 text-[11.5px] text-muted-foreground" data-testid="revenue-rule-note">
          <strong className="text-foreground">Revenue rule:</strong> {metrics.revenue_rule_note}
        </div>
      </section>

      {/* USAGE */}
      <section>
        <SectionTitle icon={Gauge} title={`Usage — top users (${rangeLabel.toLowerCase()})`} tip={tips.top_users} />
        <div className="rounded-2xl border border-border/70 bg-card/40 overflow-hidden">
          {(highUsage.items || []).length === 0 ? (
            <EmptyState text="No usage in this period yet." testId="high-usage-empty" />
          ) : (
            <table className="w-full text-sm" data-testid="high-usage-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border/60">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2 text-right">Analyses</th>
                  <th className="px-3 py-2">Last analyzed</th>
                </tr>
              </thead>
              <tbody>
                {(highUsage.items || []).map((u) => (
                  <tr key={u.user_id} className="border-b border-border/40 hover:bg-secondary/10" data-testid={`high-usage-row-${u.user_id}`}>
                    <td className="px-3 py-2 text-foreground">{u.email || u.user_id}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] uppercase">{u.plan}</Badge></td>
                    <td className="px-3 py-2 text-right tabular-nums">{u.analyses_count}</td>
                    <td className="px-3 py-2 text-muted-foreground">{fmtRelative(u.last_analyzed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* AUDIT LOG */}
      <section>
        <SectionTitle
          icon={ClipboardList}
          title="Recent admin actions"
          subtitle="Plan changes, usage resets and user deletions"
          tip={tips.audit}
        />
        <div className="rounded-2xl border border-border/70 bg-card/40 overflow-hidden">
          {(audit.items || []).length === 0 ? (
            <EmptyState text="No admin actions logged yet." testId="audit-log-empty" />
          ) : (
            <ul className="divide-y divide-border/40" data-testid="audit-log-list">
              {(audit.items || []).map((row) => (
                <li key={row.id} className="p-3 flex items-start gap-3 hover:bg-secondary/10" data-testid={`audit-row-${row.id}`}>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-border/60 bg-secondary/30 shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{row.action_type}</Badge>
                      <span className="text-foreground truncate"><span className="text-muted-foreground">by</span> <span className="font-medium">{row.admin_user_email}</span></span>
                      {row.target_user_email ? (
                        <span className="text-foreground truncate"><span className="text-muted-foreground">on</span> <span className="font-medium">{row.target_user_email}</span></span>
                      ) : null}
                      <span className="ml-auto text-[11px] text-muted-foreground">{fmtRelative(row.ts)}</span>
                    </div>
                    <AuditDiff row={row} />
                    {row.reason ? <div className="mt-1 text-[11px] text-foreground/80 italic">“{row.reason}”</div> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* SYSTEM NOTES */}
      <section>
        <div className="rounded-2xl border border-border/50 bg-secondary/10 p-4 text-[11.5px] text-muted-foreground space-y-1" data-testid="owner-dashboard-system-notes">
          <div className="flex items-center gap-2 text-foreground"><ShieldCheck className="w-3.5 h-3.5" /><strong>System notes</strong></div>
          <p>This dashboard manages users, plans, usage and subscription status only. The analysis pipeline (FMP, fair value math, AI Opportunity Score, AI narrative cache) is handled separately under the System tab.</p>
          <p>CopeCart webhooks are the only path that can mark a user as paying. Manual plan changes from this console are never counted as revenue.</p>
        </div>
      </section>
    </div>
  );
}

function rangeToDays(r) {
  switch (r) {
    case "today": return 1;
    case "7d": return 7;
    case "30d": return 30;
    case "90d": return 90;
    case "ytd": return 365;
    case "all": return 365;
    default: return 30;
  }
}
